import React, { useState, useEffect } from 'react';
import { Box, IconButton, Drawer, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import SharedSidebar from './SharedSidebar';
import { LayoutProvider } from '../contexts/LayoutContext';

interface SharedLayoutProps {
  children: React.ReactNode;
}

const SharedLayout: React.FC<SharedLayoutProps> = ({ children }) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(min-width:769px) and (max-width:1024px)');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  // Auto-collapse on tablet
  useEffect(() => {
    if (isTablet && !sidebarCollapsed) {
      setSidebarCollapsed(true);
      localStorage.setItem('sidebar_collapsed', 'true');
      window.dispatchEvent(new CustomEvent('sidebar-collapse-change'));
    }
  }, [isTablet]); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarWidth = sidebarCollapsed ? 72 : 240;

  // Listen for sidebar collapse state changes
  useEffect(() => {
    const handleStorageChange = () => {
      const collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
      setSidebarCollapsed(collapsed);
    };
    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);
    const handleCustomStorageChange = () => handleStorageChange();
    window.addEventListener('sidebar-collapse-change', handleCustomStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebar-collapse-change', handleCustomStorageChange);
    };
  }, []);

  return (
    <LayoutProvider hasSharedSidebar={true}>
      {/* Mobile: Drawer sidebar */}
      {isMobile ? (
        <>
          <Drawer
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: 260,
                backgroundColor: '#0A2540',
                border: 'none',
              },
            }}
            ModalProps={{ keepMounted: true }}
          >
            <SharedSidebar onCollapseChange={() => {}} mobileMode onMobileClose={() => setMobileDrawerOpen(false)} />
          </Drawer>

          {/* Mobile top bar with hamburger */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: 56,
              backgroundColor: '#0A2540',
              borderBottom: '1px solid #1e2139',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              zIndex: 1100,
              gap: 1.5,
            }}
          >
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ color: '#00C896' }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src="/saheeh_logo_w.svg"
              alt="Saheeh"
              sx={{ height: 36, width: 'auto' }}
            />
          </Box>

          {/* Main content - full width on mobile */}
          <Box
            sx={{
              marginTop: '56px',
              minHeight: 'calc(100vh - 56px)',
              backgroundColor: '#1e2139',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ flex: 1 }}>
              {children}
            </Box>
            {/* Mobile Footer Branding */}
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                pb: 5,
                borderTop: '1px solid #2a2d3e',
                backgroundColor: '#0A2540',
              }}
            >
              <Box component="img" src="/saheeh_logo_w.svg" alt="Saheeh" sx={{ height: 28, width: 'auto', mx: 'auto', mb: 1, opacity: 0.5 }} />
              <Box sx={{ fontSize: '0.65rem', color: '#4a4d62', fontFamily: '"Poppins"', letterSpacing: '0.5px' }}>
                Saheeh POS v2.1 &middot; saheehpos.com
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <>
          {/* Desktop/Tablet: Fixed sidebar */}
          <SharedSidebar onCollapseChange={setSidebarCollapsed} />
          <Box
            sx={{
              marginLeft: `${sidebarWidth}px`,
              width: `calc(100% - ${sidebarWidth}px)`,
              minHeight: '100vh',
              backgroundColor: '#1e2139',
              transition: 'margin-left 0.2s ease, width 0.2s ease',
              position: 'relative',
              zIndex: 1,
              overflowX: 'hidden',
            }}
          >
            {children}
          </Box>
        </>
      )}
    </LayoutProvider>
  );
};

export default SharedLayout;
